<template>
  <div class="gs-gateways-step">
    <section class="gs-wizard-card gs-gateways-step__summary">
      <div class="gs-gateways-step__summary-head">
        <div>
          <h2>Payment routing</h2>
          <p>Choose which connected merchant accounts can process this product.</p>
        </div>
        <div
          class="gs-gateways-step__total"
          :class="{ 'is-complete': currentGatewayTotal === 100, 'is-warning': currentGatewayTotal !== 100 }"
        >
          <strong>{{ currentGatewayTotal }}%</strong>
          <span>allocated</span>
        </div>
      </div>

      <div class="gs-gateways-step__tabs">
        <button
          type="button"
          :class="{ 'is-active': gatewaysType === 'creditcard' }"
          @click="gatewaysType = 'creditcard'"
        >
          Credit Cards
        </button>
        <button
          type="button"
          :class="{ 'is-active': gatewaysType === 'paypal' }"
          @click="gatewaysType = 'paypal'"
        >
          PayPal
        </button>
      </div>

      <div class="gs-gateways-step__routing-bar" aria-hidden="true">
        <span
          v-for="account in currentConnectedAccounts"
          :key="`${account.key}-segment`"
          :style="{
            width: `${Math.max(account.weightage, 0)}%`,
            backgroundColor: account.routingColor,
          }"
        ></span>
      </div>
      <p
        v-if="currentConnectedAccounts.length && currentGatewayTotal !== 100"
        class="gs-gateways-step__allocation-warning"
      >
        Allocation must total 100% before this step is complete. Current total:
        <strong>{{ currentGatewayTotal }}%</strong>.
      </p>

      <div class="gs-gateways-step__add-existing">
        <div>
          <strong>Add connected account to rotation</strong>
          <span>{{ gatewayRotationHelpText }}</span>
        </div>
        <div v-if="availableGatewayAccounts.length" class="gs-gateways-step__account-picker">
          <button
            type="button"
            class="gs-gateways-step__picker-trigger"
            @click="toggleGatewayAccountPicker"
          >
            <span>{{ selectedGatewayIntegrationLabel }}</span>
            <gs-icon name="chevron-down" size="16"></gs-icon>
          </button>
          <div v-if="showGatewayAccountPicker" class="gs-gateways-step__picker-menu">
            <button
              v-for="integration in availableGatewayAccounts"
              :key="integration.id"
              type="button"
              @click="addGatewayIntegrationFromPicker(integration)"
            >
              <strong>{{ gatewayAccountDisplayName(integration) }}</strong>
              <small>{{ gatewayAccountSummary(integration) }}</small>
            </button>
          </div>
        </div>
        <div v-else class="gs-gateways-step__empty-picker">
          {{ gatewayEmptyPickerLabel }}
        </div>
        <gs-button type="button" variant="outline" @click="goToSetupGateways">
          Add new gateway
        </gs-button>
        <gs-button
          type="button"
          variant="ghost"
          size="sm"
          class="gs-gateways-step__refresh-action"
          :disabled="fetchingIntegrations"
          @click="reloadIntegrations"
        >
          {{ fetchingIntegrations ? 'Refreshing...' : 'Refresh accounts' }}
        </gs-button>
      </div>

      <div v-if="currentConnectedAccounts.length">
        <article
          v-for="account in currentConnectedAccounts"
          :key="account.key"
          class="gs-gateways-step__account"
          :class="{ 'is-active': expandedGatewayAccountKey === account.key }"
          :style="{ '--routing-color': account.routingColor }"
        >
          <button
            type="button"
            class="gs-gateways-step__account-row"
            @click="toggleGatewayAccount(account.key)"
          >
            <span class="gs-gateways-step__brand">
              <component
                :is="gatewayLogoComponent(account.platform)"
                v-if="gatewayLogoComponent(account.platform)"
                class="gs-gateways-step__logo-icon"
              />
              <img v-else :src="gatewayLogoUrl(account.platform)" :alt="account.platformLabel" />
              <span>
                <b class="gs-gateways-step__legend-dot"></b>
                <strong>{{ account.name }}</strong>
                <small>{{ account.accountSummary }}</small>
                <em v-if="account.identifier">{{ account.identifier }}</em>
              </span>
            </span>
            <span class="gs-gateways-step__share">
              <strong>{{ account.weightage }}%</strong>
              <small>{{ gatewaysType === 'paypal' ? 'rotation' : 'distribution' }}</small>
            </span>
            <gs-icon :name="expandedGatewayAccountKey === account.key ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
          </button>

          <div v-if="expandedGatewayAccountKey === account.key" class="gs-gateways-step__account-details">
            <div class="gs-gateways-step__account-meta">
              <span>
                <small>Gateway type</small>
                <strong>{{ account.platformLabel }}</strong>
              </span>
              <span>
                <small>Account name</small>
                <strong>{{ account.name }}</strong>
              </span>
              <span>
                <small>Allocation</small>
                <strong>{{ account.weightage }}%</strong>
              </span>
              <span>
                <small>Integration ID</small>
                <strong>{{ account.id }}</strong>
              </span>
              <span v-if="account.identifier">
                <small>Identifier</small>
                <strong>{{ account.identifier }}</strong>
              </span>
            </div>
            <label class="gs-gateways-step__allocation">
              <span>{{ gatewaysType === 'paypal' ? 'Rotation percentage' : 'Distribution percentage' }}</span>
              <input
                v-model="account.source.weightage"
                type="number"
                min="0"
                max="100"
                @input="touchValidation"
              />
            </label>
            <div class="gs-gateways-step__account-actions">
              <gs-button type="button" variant="outline" size="sm" @click="normalizeGatewayWeights(gatewaysType)">
                Balance evenly
              </gs-button>
              <gs-button type="button" variant="danger" size="sm" @click="removeGatewayAccount(account)">
                Remove
              </gs-button>
            </div>
          </div>
        </article>

        <article v-if="gatewaysType === 'paypal'" class="gs-gateways-step__risk">
          <div>
            <strong>Risk Mitigation</strong>
            <span>Control how often PayPal is offered during checkout.</span>
          </div>
          <gd-dropdown
            v-model="localModel.paypal_risk_percentage"
            size="full"
            :options="paypalRiskPercentageOptions"
          />
        </article>
      </div>

      <div v-else class="gs-gateways-step__connect">
        <article
          v-for="provider in visibleProviderCards"
          :key="provider.name"
          class="gs-gateways-step__provider"
        >
          <span>
            <component
              :is="gatewayLogoComponent(provider.name)"
              v-if="gatewayLogoComponent(provider.name)"
              class="gs-gateways-step__logo-icon"
            />
            <img v-else :src="gatewayLogoUrl(provider.name)" :alt="provider.display_name" />
            <strong>{{ provider.display_name }}</strong>
            <small>{{ provider.description }}</small>
          </span>
          <gs-button type="button" variant="primary" size="sm" @click="openConnectGatewayModal(provider.type)">
            Connect
          </gs-button>
        </article>

        <gs-button
          v-if="hiddenProviderCards.length"
          type="button"
          variant="ghost"
          size="sm"
          class="gs-gateways-step__more"
          @click="showMoreGatewayProviders = !showMoreGatewayProviders"
        >
          {{ showMoreGatewayProviders ? 'Hide more options' : 'See more options' }}
        </gs-button>
      </div>
    </section>

    <gs-modal
      v-model="showConnectGatewayModal"
      title="Connect Payment Gateway"
      subtitle="Select a connected merchant account for this product, or open settings to add a new one."
      size="xl"
    >
      <section class="gs-gateways-step__modal">
        <div class="gs-gateways-step__modal-layout">
          <div class="gs-gateways-step__modal-main">
            <strong class="gs-gateways-step__modal-label">Available accounts</strong>
            <div v-if="selectableIntegrations.length" class="gs-gateways-step__modal-grid">
              <button
                v-for="integration in selectableIntegrations"
                :key="integration.id"
                type="button"
                class="gs-gateways-step__gateway-card"
                :class="{ 'is-selected': isIntegrationSelected(integration) }"
                :disabled="isIntegrationSelected(integration)"
                @click="addGatewayIntegration(integration)"
              >
                <component
                  :is="gatewayLogoComponent(integration.platform)"
                  v-if="gatewayLogoComponent(integration.platform)"
                  class="gs-gateways-step__logo-icon"
                />
                <img v-else :src="gatewayLogoUrl(integration.platform)" :alt="gatewayPlatformLabel(integration.platform)" />
                <strong>{{ gatewayAccountDisplayName(integration) }}</strong>
                <small>{{ gatewayAccountSummary(integration) }}</small>
                <gs-icon v-if="isIntegrationSelected(integration)" name="check" size="16"></gs-icon>
              </button>
            </div>

            <div v-else class="gs-gateways-step__modal-empty">
              <strong>No connected {{ gatewayTypeLabel }} accounts yet</strong>
              <span>Add one in payment gateway settings, then refresh this list.</span>
            </div>
          </div>

          <aside class="gs-gateways-step__modal-aside">
            <strong>Add a new gateway</strong>
            <p>Connect another {{ gatewayTypeLabel }} account in settings, then return here to add it into this product's routing.</p>
            <gs-button type="button" variant="primary" class="gs-gateways-step__modal-primary" @click="goToSetupGateways">
              Open gateway settings
            </gs-button>
            <div class="gs-gateways-step__supported">
              <strong>Supported {{ gatewayTypeLabel }} gateways</strong>
              <div>
                <span v-for="provider in supportedProviderCards" :key="provider.name">
                  <component
                    :is="gatewayLogoComponent(provider.name)"
                    v-if="gatewayLogoComponent(provider.name)"
                    class="gs-gateways-step__logo-icon"
                  />
                  <img v-else :src="gatewayLogoUrl(provider.name)" :alt="provider.display_name" />
                  {{ provider.display_name }}
                </span>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </gs-modal>
  </div>
</template>

<script>
import axios from 'axios';
import {
  required,
  numeric,
  minValue,
  maxValue,
} from 'vuelidate/lib/validators';
import createLinkMixin from '@/mixins/createLinkMixin';
import CreditCardIcon from '@/assets/images/icons/CreditCardIcon.svg';
import AuthorizeNetLogo from '@/assets/images/logo/authorize.net-integration.png';
import BraintreeLogo from '@/assets/images/logo/braintree-integration.png';
import GroovePayLogo from '@/assets/images/logo/groovepay-integration.png';
import GroovePayAutomatedLogo from '@/assets/images/logo/groovepay_automated-integration.png';
import MollieLogo from '@/assets/images/logo/mollie-integration.png';
import NmiLogo from '@/assets/images/logo/nmi-integration.png';
import PaypalLogo from '@/assets/images/logo/paypal-integration.png';
import PaypalCommerceLogo from '@/assets/images/logo/paypal_commerce-integration.png';
import PaystackLogo from '@/assets/images/logo/paystack-integration.png';
import StripeLogo from '@/assets/images/logo/stripe-integration.png';

export default {
  name: 'Step3',
  mixins: [createLinkMixin],
  props: {
    model: {
      required: true,
    },
    gatewayIntegrations: {
      required: true,
    },
  },
  data() {
    return {
      fetchingIntegrations: false,
      integrationsLoaderKey: 1,
      localModel: this.model,
      gatewaysType: 'creditcard',
      showConnectGatewayModal: false,
      showMoreGatewayProviders: false,
      expandedGatewayAccountKey: null,
      showGatewayAccountPicker: false,
      availablePaymentIntegrations: [],
      formFieldsResponse: null,
    };
  },
  validations: {
    localModel: {
      paypal_processing_accounts: {
        $each: {
          weightage: {
            required,
            numeric,
            minValue: minValue(0),
            maxValue: maxValue(100),
            totalHundredPercent() {
              const self = this;
              let totalWeightage = 0;

              for (
                let i = 0;
                i < self.localModel.paypal_processing_accounts.length;
                i++
              ) {
                totalWeightage += parseInt(
                  self.localModel.paypal_processing_accounts[i].weightage,
                  10
                );
              }

              if (totalWeightage == 100) {
                return true;
              }
              return false;
            },
          },
        },
      },
      credit_card_processing_accounts: {
        $each: {
          weightage: {
            required,
            numeric,
            minValue: minValue(0),
            maxValue: maxValue(100),
            totalHundredPercent() {
              const self = this;
              let totalWeightage = 0;

              for (
                let i = 0;
                i < self.localModel.credit_card_processing_accounts.length;
                i++
              ) {
                totalWeightage += parseInt(
                  self.localModel.credit_card_processing_accounts[i].weightage,
                  10
                );
              }

              if (totalWeightage == 100) {
                return true;
              }
              return false;
            },
          },
        },
      },
    },
  },
  computed: {
    paypalRiskPercentageOptions() {
      const options = [];

      for (let i = 10; i <= 100; i += 10) {
        options.push({ value: i, text: `${i}%` });
      }

      return options;
    },
    currentProcessingAccounts() {
      this.ensureGatewayArrays();
      return this.gatewaysType === 'paypal'
        ? this.localModel.paypal_processing_accounts
        : this.localModel.credit_card_processing_accounts;
    },
    currentConnectedAccounts() {
      return this.currentProcessingAccounts.map((account, index) => {
        const integration = this.integrationForId(account.id);
        const platform = integration ? integration.platform : this.gatewaysType;
        const accountName = integration
          ? this.gatewayAccountDisplayName(integration)
          : `Saved ${this.gatewaysType === 'paypal' ? 'PayPal' : 'credit card'} account`;
        const accountSummary = integration
          ? this.gatewayAccountSummary(integration)
          : `Saved integration ID ${account.id}`;

        return {
          id: account.id,
          key: `${this.gatewaysType}-${account.id}`,
          name: accountName,
          accountSummary,
          identifier: this.gatewayAccountIdentifier(integration),
          platform,
          platformLabel: this.gatewayPlatformLabel(platform),
          routingColor: this.routingSegmentColor(index),
          weightage: account.weightage || 0,
          source: account,
        };
      });
    },
    currentGatewayTotal() {
      return this.currentProcessingAccounts.reduce((total, account) => {
        return total + Number(account.weightage || 0);
      }, 0);
    },
    selectableIntegrations() {
      return this.gatewayIntegrations.filter((integration) => {
        return this.gatewayTypeForPlatform(integration.platform) === this.gatewaysType;
      });
    },
    availableGatewayAccounts() {
      const selectedIds = this.currentProcessingAccounts.map((account) => String(account.id));

      return this.selectableIntegrations.filter((integration) => {
        return !selectedIds.includes(String(integration.id));
      });
    },
    gatewayTypeLabel() {
      return this.gatewaysType === 'paypal' ? 'PayPal' : 'credit card';
    },
    hasConnectedGatewayAccounts() {
      return this.selectableIntegrations.length > 0;
    },
    gatewayRotationHelpText() {
      if (this.availableGatewayAccounts.length) {
        return `Select one of your connected ${this.gatewayTypeLabel} accounts and add it to this product's routing.`;
      }

      if (this.hasConnectedGatewayAccounts) {
        return `Every connected ${this.gatewayTypeLabel} account is already in this product's routing.`;
      }

      return `You do not have any connected ${this.gatewayTypeLabel} accounts yet. Connect one in gateway settings first.`;
    },
    gatewayEmptyPickerLabel() {
      if (this.hasConnectedGatewayAccounts) {
        return 'All connected accounts are already in rotation';
      }

      return `No connected ${this.gatewayTypeLabel} accounts yet`;
    },
    selectedGatewayIntegrationLabel() {
      if (!this.availableGatewayAccounts.length) {
        return 'No connected accounts available';
      }

      return `Select ${this.gatewayTypeLabel} account`;
    },
    supportedProviderCards() {
      const providers = this.availablePaymentIntegrations.length
        ? this.availablePaymentIntegrations
        : this.fallbackSupportedProviders;

      return providers
        .map((provider) => ({
          name: provider.name,
          display_name: provider.display_name || this.gatewayPlatformLabel(provider.name),
          description: this.providerDescription(provider.name),
          type: this.gatewayTypeForPlatform(provider.name),
        }))
        .filter((provider) => provider.type === this.gatewaysType);
    },
    visibleProviderCards() {
      return this.showMoreGatewayProviders
        ? this.supportedProviderCards
        : this.supportedProviderCards.slice(0, 2);
    },
    hiddenProviderCards() {
      return this.supportedProviderCards.slice(2);
    },
    fallbackSupportedProviders() {
      return [
        { name: 'groove_pay', display_name: 'GroovePay' },
        { name: 'groovepay_automated', display_name: 'GroovePay' },
        { name: 'paypal_commerce', display_name: 'PayPal' },
        { name: 'paypal', display_name: 'PayPal' },
        { name: 'stripe', display_name: 'Stripe' },
        { name: 'authorize.net', display_name: 'Authorize.net' },
        { name: 'nmi', display_name: 'NMI' },
        { name: 'braintree', display_name: 'Braintree' },
        { name: 'paystack', display_name: 'Paystack' },
        { name: 'mollie', display_name: 'Mollie' },
        { name: 'spreedly', display_name: 'Spreedly' },
        { name: 'stax', display_name: 'Stax' },
      ];
    },

    paypalAddedAccounts() {
      this.ensureGatewayArrays();
      return this.localModel.paypal_processing_accounts.map((a) => a.id);
    },

    cardAddedAccounts() {
      this.ensureGatewayArrays();
      return this.localModel.credit_card_processing_accounts.map((a) => a.id);
    },

    gatewayTypesErrorTabs() {
      const gatewayTypeErrorTabs = [];

      if (this.$v.localModel.paypal_processing_accounts.$error) {
        gatewayTypeErrorTabs.push('paypal');
      }

      if (this.$v.localModel.credit_card_processing_accounts.$error) {
        gatewayTypeErrorTabs.push('creditcard');
      }

      return gatewayTypeErrorTabs;
    },
  },
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
    gatewaysType() {
      this.showGatewayAccountPicker = false;
      this.showMoreGatewayProviders = false;
    },
  },
  async created() {
    this.ensureGatewayArrays();
    if (window.location.hash === '#step3') {
      window.location.hash = '';
    }
    await this.fetchSupportedPaymentIntegrations();
  },
  methods: {
    ensureGatewayArrays() {
      if (!Array.isArray(this.localModel.paypal_processing_accounts)) {
        this.localModel.paypal_processing_accounts = [];
      }

      if (!Array.isArray(this.localModel.credit_card_processing_accounts)) {
        this.localModel.credit_card_processing_accounts = [];
      }
    },

    async fetchSupportedPaymentIntegrations() {
      try {
        const { data } = await axios.get(`${window.config.apiCoreUrl}/paymentintegrationdata`);
        this.availablePaymentIntegrations = data;
      } catch (e) {
        this.availablePaymentIntegrations = [];
      }
    },

    goToSetupGateways() {
      const routeData = this.$router.resolve({ name: 'vendor_settings' });
      window.open(routeData.href, '_blank');
    },

    openGroovepay() {
      const payAppTitle = this.whitelabelAppData('pay').title.toLowerCase();
      const sellAppTitle = this.whitelabelAppData('sell').title.toLowerCase();

      this.openLink(`https://app.${payAppTitle}.com/?refId=${sellAppTitle}`, '_blank');
    },

    async reloadIntegrations() {
      this.fetchingIntegrations = true;
      this.integrationsLoaderKey++;
      this.$emit('reloadIntegrations');
      setTimeout(() => {
        this.fetchingIntegrations = false;
        this.integrationsLoaderKey++;
      }, 1000);
    },

    validate() {
      this.$v.localModel.$touch();
      const isValid = !this.$v.localModel.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    integrationName(id) {
      const integration = this.gatewayIntegrations.find((obj) => {
        return String(obj.id) === String(id);
      });
      if (!integration) {
        return '';
      }

      return integration.platform === 'commas'
        ? 'ScaleGG Pay'
        : integration.name;
    },

    integrationForId(id) {
      return this.gatewayIntegrations.find((integration) => {
        return String(integration.id) === String(id);
      });
    },

    gatewayAccountIdentifier(integration) {
      if (!integration || !integration.details) {
        return '';
      }

      const details = integration.details;
      const identifierKeys = [
        'email',
        'paypal_email',
        'merchant_id',
        'merchantId',
        'account_id',
        'accountId',
        'gateway_id',
        'gatewayId',
        'publishable_key',
        'public_key',
      ];
      const key = identifierKeys.find((candidate) => details[candidate]);

      if (!key) {
        return '';
      }

      return this.maskGatewayIdentifier(String(details[key]));
    },

    gatewayAccountDisplayName(integration) {
      if (!integration) {
        return '';
      }

      if (integration.platform === 'commas') {
        return 'ScaleGG Pay';
      }

      const details = integration.details || {};
      return (
        integration.name ||
        integration.title ||
        integration.display_name ||
        details.name ||
        details.account_name ||
        details.nickname ||
        this.gatewayPlatformLabel(integration.platform)
      );
    },

    gatewayAccountSummary(integration) {
      const provider = this.gatewayPlatformLabel(integration.platform);
      const identifier = this.gatewayAccountIdentifier(integration);

      return identifier ? `${provider} account - ${identifier}` : `${provider} account`;
    },

    maskGatewayIdentifier(value) {
      if (value.includes('@')) {
        const [name, domain] = value.split('@');
        return `${name.slice(0, 2)}***@${domain}`;
      }

      if (value.length > 14) {
        return `${value.slice(0, 6)}...${value.slice(-4)}`;
      }

      return value;
    },

    gatewayTypeForPlatform(platform) {
      return platform === 'paypal' || platform === 'paypal_commerce'
        ? 'paypal'
        : 'creditcard';
    },

    gatewayPlatformLabel(platform) {
      const labels = {
        'authorize.net': 'Authorize.net',
        braintree: 'Braintree',
        creditcard: 'Credit card',
        commas: 'ScaleGG Pay',
        groove_pay: 'GroovePay',
        groovepay_automated: 'GroovePay',
        mollie: 'Mollie',
        nmi: 'NMI',
        paypal: 'PayPal',
        paypal_commerce: 'PayPal',
        paystack: 'Paystack',
        spreedly: 'Spreedly',
        stax: 'Stax',
        stripe: 'Stripe',
      };

      return labels[platform] || platform;
    },

    providerDescription(platform) {
      if (platform === 'paypal' || platform === 'paypal_commerce') {
        return 'PayPal Express Checkout is a fast, easy way for buyers to pay with PayPal.';
      }

      if (platform === 'groove_pay' || platform === 'groovepay_automated') {
        return 'The best and most affordable way to accept credit cards.';
      }

      return 'Connect this supported merchant account to accept payments for this product.';
    },

    gatewayLogoUrl(platform) {
      const logos = {
        'authorize.net': AuthorizeNetLogo,
        braintree: BraintreeLogo,
        groove_pay: GroovePayLogo,
        groovepay_automated: GroovePayAutomatedLogo,
        mollie: MollieLogo,
        nmi: NmiLogo,
        paypal: PaypalLogo,
        paypal_commerce: PaypalCommerceLogo,
        paystack: PaystackLogo,
        stripe: StripeLogo,
      };

      return logos[platform] || null;
    },

    gatewayLogoComponent(platform) {
      if (platform === 'creditcard' || !this.gatewayLogoUrl(platform)) {
        return CreditCardIcon;
      }

      return null;
    },

    routingSegmentColor(index) {
      const colors = ['var(--gs-primary)', '#7b61ff', '#38b978', '#f4b740', '#36a3ff', '#9b6bff'];
      return colors[index % colors.length];
    },

    openConnectGatewayModal(type = this.gatewaysType) {
      this.gatewaysType = type;
      this.showConnectGatewayModal = true;
    },

    toggleGatewayAccountPicker() {
      if (!this.availableGatewayAccounts.length) {
        return;
      }

      this.showGatewayAccountPicker = !this.showGatewayAccountPicker;
    },

    addGatewayIntegrationFromPicker(integration) {
      this.addGatewayIntegration(integration);
      this.showGatewayAccountPicker = false;
    },

    addGatewayIntegration(integration) {
      if (this.isIntegrationSelected(integration)) {
        return;
      }

      if (this.gatewayTypeForPlatform(integration.platform) === 'paypal') {
        this.addPaypalGateway(integration.id);
      } else {
        this.addCardGateway(integration.id);
      }

      this.normalizeGatewayWeights(this.gatewayTypeForPlatform(integration.platform));
    },

    isIntegrationSelected(integration) {
      const selectedAccounts = this.gatewayTypeForPlatform(integration.platform) === 'paypal'
        ? this.localModel.paypal_processing_accounts
        : this.localModel.credit_card_processing_accounts;

      return selectedAccounts.some((account) => String(account.id) === String(integration.id));
    },

    toggleGatewayAccount(key) {
      this.expandedGatewayAccountKey =
        this.expandedGatewayAccountKey === key ? null : key;
    },

    touchValidation() {
      this.$v.localModel.$touch();
    },

    addPaypalGateway(id) {
      if (this.paypalAddedAccounts.includes(id)) {
        return;
      }

      this.localModel.paypal_processing_accounts.push({
        id,
        weightage: 0,
      });
    },

    addCardGateway(id) {
      if (this.cardAddedAccounts.includes(id)) {
        return;
      }

      this.localModel.credit_card_processing_accounts.push({
        id,
        weightage: 0,
      });
    },

    removeGatewayAccount(account) {
      if (this.gatewaysType === 'paypal') {
        this.localModel.paypal_processing_accounts =
          this.localModel.paypal_processing_accounts.filter((obj) => obj.id !== account.id);
      } else {
        this.localModel.credit_card_processing_accounts =
          this.localModel.credit_card_processing_accounts.filter((obj) => obj.id !== account.id);
      }

      this.expandedGatewayAccountKey = null;
      this.normalizeGatewayWeights(this.gatewaysType);
    },

    normalizeGatewayWeights(type) {
      const accounts = type === 'paypal'
        ? this.localModel.paypal_processing_accounts
        : this.localModel.credit_card_processing_accounts;

      if (!accounts.length) {
        return;
      }

      const base = Math.floor(100 / accounts.length);
      let remainder = 100 - (base * accounts.length);

      accounts.forEach((account) => {
        account.weightage = base + (remainder > 0 ? 1 : 0);
        remainder--;
      });
    },
  },
};
</script>
<style lang="scss">
.gs-gateways-step {
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 700px;
  margin: 0 auto;
}


.gs-gateways-step__summary-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;
}

.gs-gateways-step__summary h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-gateways-step__summary-head p {
  margin: 4px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  line-height: 1.4;
}

.gs-gateways-step__total {
  display: grid;
  place-items: center;
  min-width: 98px;
  min-height: 64px;
  padding: 10px 16px;
  color: var(--gs-primary);
  border: 1px solid var(--gs-primary);
  border-radius: var(--gs-radius-md);
  background: var(--gs-primary-soft);
}

.gs-gateways-step__total.is-complete {
  color: var(--gs-success);
  border-color: rgba(56, 185, 120, .24);
  background: var(--gs-success-soft);
}

.gs-gateways-step__total strong {
  color: inherit;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
}

.gs-gateways-step__total span {
  margin-top: 5px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.gs-gateways-step__picker-trigger,
.gs-gateways-step__picker-menu button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 16px;
  color: var(--gs-primary-foreground);
  font-size: 14px;
  font-weight: 600;
  border: 0;
  background: var(--gs-primary);
  cursor: pointer;
}

.gs-gateways-step__picker-trigger {
  border-radius: var(--gs-radius-md);
}

.gs-gateways-step__picker-menu button {
  border-radius: var(--gs-radius-sm);
}

.gs-gateways-step__refresh-action {
  grid-column: 2 / -1;
  justify-self: end;
}

.gs-gateways-step__tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
}

.gs-gateways-step__tabs button {
  min-height: 36px;
  padding: 0 14px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-gateways-step__tabs button.is-active {
  color: var(--gs-active);
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-gateways-step__routing-bar {
  display: flex;
  gap: 3px;
  width: 100%;
  height: 10px;
  margin-bottom: 18px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--gs-border);
}

.gs-gateways-step__routing-bar span {
  min-width: 3px;
}

.gs-gateways-step__allocation-warning {
  margin: -8px 0 18px;
  padding: 10px 12px;
  color: #9b4a12;
  font-size: 13px;
  line-height: 1.4;
  background: #fff8ed;
  border: 1px solid #f6d3a8;
  border-radius: var(--gs-radius-sm);
}

.gs-gateways-step__allocation-warning strong {
  color: #9b4a12;
  font-weight: 700;
}

.gs-gateways-step__add-existing {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 280px) auto;
  gap: 10px;
  align-items: end;
  margin-bottom: 18px;
  padding: 14px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-gateways-step__add-existing strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-gateways-step__add-existing span {
  display: block;
  margin-top: 3px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.35;
}

.gs-gateways-step__account-picker {
  position: relative;
  min-width: 0;
}

.gs-gateways-step__empty-picker {
  display: flex;
  align-items: center;
  min-height: 42px;
  min-width: 0;
  padding: 0 14px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  background: #ffffff;
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-gateways-step__picker-trigger {
  justify-content: space-between !important;
  width: 100%;
  min-height: 42px;
  padding: 0 12px;
  color: var(--gs-foreground) !important;
  background: #ffffff !important;
  border: 1px solid var(--gs-border) !important;
}

.gs-gateways-step__picker-trigger span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-gateways-step__picker-trigger i {
  flex: 0 0 auto;
  margin-left: 10px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
}

.gs-gateways-step__picker-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 260px;
  overflow: auto;
  padding: 6px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-md);
}

.gs-gateways-step__picker-menu button {
  display: block;
  width: 100%;
  min-height: auto;
  padding: 10px 12px;
  color: var(--gs-foreground);
  text-align: left;
  background: #ffffff;
  border: 0;
  border-radius: var(--gs-radius-sm);
}

.gs-gateways-step__picker-menu button:hover {
  background: var(--gs-surface-subtle);
}

.gs-gateways-step__picker-menu strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.3;
}

.gs-gateways-step__picker-menu small {
  display: block;
  margin-top: 3px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.35;
}

.gs-gateways-step__account {
  position: relative;
  margin-top: 12px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  overflow: hidden;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.gs-gateways-step__account::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 5px;
  background: var(--routing-color);
}

.gs-gateways-step__account.is-active {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-gateways-step__account-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 118px 18px;
  column-gap: 18px;
  align-items: center;
  width: 100%;
  min-height: 82px;
  padding: 0 18px;
  color: var(--gs-muted-foreground);
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-gateways-step__brand {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 14px;
}

.gs-gateways-step__brand img,
.gs-gateways-step__brand .gs-gateways-step__logo-icon,
.gs-gateways-step__provider img,
.gs-gateways-step__provider .gs-gateways-step__logo-icon,
.gs-gateways-step__gateway-card img,
.gs-gateways-step__gateway-card .gs-gateways-step__logo-icon,
.gs-gateways-step__supported img {
  object-fit: contain;
}

.gs-gateways-step__brand img,
.gs-gateways-step__brand .gs-gateways-step__logo-icon {
  width: 76px;
  height: 34px;
  color: var(--gs-muted-foreground);
}

.gs-gateways-step__brand span {
  position: relative;
  min-width: 0;
  padding-left: 18px;
}

.gs-gateways-step__legend-dot {
  position: absolute;
  top: 6px;
  left: 0;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--routing-color);
}

.gs-gateways-step__brand strong {
  white-space: normal;
}

.gs-gateways-step__brand strong,
.gs-gateways-step__provider strong,
.gs-gateways-step__gateway-card strong,
.gs-gateways-step__risk strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-gateways-step__brand small,
.gs-gateways-step__brand em,
.gs-gateways-step__provider small,
.gs-gateways-step__gateway-card small,
.gs-gateways-step__risk span {
  display: block;
  margin-top: 3px;
  overflow: hidden;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.4;
  text-overflow: ellipsis;
}

.gs-gateways-step__brand em {
  margin-top: 2px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-style: normal;
}

.gs-gateways-step__share {
  display: grid;
  justify-items: end;
  color: var(--gs-foreground);
}

.gs-gateways-step__share strong {
  color: var(--gs-foreground);
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.gs-gateways-step__share small {
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.gs-gateways-step__account-row i {
  color: var(--gs-muted-foreground);
  font-size: 15px;
}

.gs-gateways-step__account-details {
  padding: 18px;
  border-top: 1px solid var(--gs-border);
}

.gs-gateways-step__account-meta {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.gs-gateways-step__account-meta small {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

.gs-gateways-step__account-meta strong {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-gateways-step__allocation {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px;
  gap: 14px;
  align-items: center;
  margin: 0;
}

.gs-gateways-step__allocation span {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 500;
}

.gs-gateways-step__allocation input {
  min-height: 42px;
  padding: 0 12px;
  color: var(--gs-foreground);
  font-size: 16px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-gateways-step__account-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.gs-gateways-step__connect {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gs-gateways-step__provider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 86px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-gateways-step__provider > span {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  column-gap: 16px;
  align-items: center;
  min-width: 0;
}

.gs-gateways-step__provider img,
.gs-gateways-step__provider .gs-gateways-step__logo-icon {
  width: 104px;
  height: 36px;
  grid-row: span 2;
  color: var(--gs-muted-foreground);
}

.gs-gateways-step__more {
  align-self: center;
}

.gs-gateways-step__risk {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px;
  gap: 18px;
  align-items: center;
  margin-top: 12px;
  padding: 18px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}


/* Content wrapper inside the kit modal body, which ships no padding. */
.gs-gateways-step__modal {
  position: static;
  width: 100%;
  padding: 24px 28px 32px;
  background: transparent;
}

.gs-gateways-step__modal-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 20px;
  align-items: start;
}

.gs-gateways-step__modal-main,
.gs-gateways-step__modal-aside {
  min-width: 0;
}

.gs-gateways-step__modal-label {
  display: block;
  margin-bottom: 12px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 700;
}

.gs-gateways-step__modal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.gs-gateways-step__gateway-card {
  position: relative;
  min-height: 140px;
  padding: 18px;
  color: var(--gs-muted-foreground);
  text-align: left;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
  box-shadow: var(--gs-shadow-sm);
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}

.gs-gateways-step__gateway-card:hover:not(:disabled) {
  border-color: var(--gs-active);
  box-shadow: var(--gs-shadow-md);
}

.gs-gateways-step__gateway-card:disabled {
  cursor: not-allowed;
  opacity: .72;
}

.gs-gateways-step__gateway-card.is-selected {
  border-color: var(--gs-active);
  background: var(--gs-active-soft);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-gateways-step__gateway-card img,
.gs-gateways-step__gateway-card .gs-gateways-step__logo-icon {
  width: 96px;
  height: 34px;
  margin: 0 0 14px;
  color: var(--gs-muted-foreground);
}

.gs-gateways-step__gateway-card.is-add span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-bottom: 10px;
  color: var(--gs-primary-foreground);
  font-size: 24px;
  border-radius: var(--gs-radius-sm);
  background: var(--gs-primary);
}

.gs-gateways-step__gateway-card i {
  position: absolute;
  top: 10px;
  right: 10px;
  color: var(--gs-active);
}

.gs-gateways-step__modal-empty {
  display: grid;
  gap: 6px;
  min-height: 140px;
  padding: 22px;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-gateways-step__modal-empty strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-gateways-step__modal-aside {
  padding: 18px;
  background: var(--gs-primary-soft);
  border: 1px solid var(--gs-primary);
  border-radius: var(--gs-radius-md);
}

.gs-gateways-step__modal-aside > strong {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-gateways-step__modal-aside > p {
  margin: 0 0 16px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-gateways-step__modal-primary {
  width: 100%;
}

.gs-gateways-step__supported {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--gs-border);
}

.gs-gateways-step__supported > strong {
  display: block;
  margin-bottom: 12px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-gateways-step__supported > div {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gs-gateways-step__supported span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 10px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: 999px;
}

.gs-gateways-step__supported img,
.gs-gateways-step__supported .gs-gateways-step__logo-icon {
  width: 44px;
  height: 18px;
  color: var(--gs-muted-foreground);
}

@media (max-width: 860px) {
  .gs-gateways-step__add-existing,
  .gs-gateways-step__modal-layout,
  .gs-gateways-step__modal-grid {
    grid-template-columns: 1fr;
  }

  .gs-gateways-step__refresh-action {
    grid-column: auto;
    justify-self: start;
  }

}
</style>
